import React, { useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  useTheme,
  MenuItem,
  Paper
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Controller,
  SubmitHandler,
  useForm,
  useFieldArray
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useUpdatePurchaseOrder } from "../../../hooks/api/usePurchaseOrder";
import { PurchaseOrderFormData } from "../types/purchaseOrder";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { SampleSuppliers } from "../constants/mockData";
import { purchaseOrderSchema } from "../constants/validationSchemas";
import type { EditPurchaseOrderModalProps } from "../interfaces/modals";
import { useReportError } from "../../../hooks/useReportError";

const EditPurchaseOrderModal: React.FC<EditPurchaseOrderModalProps> = ({
  open,
  onClose,
  purchaseOrder
}) => {
  const { t } = useKivunovaTranslation();
  const updatePurchaseOrder = useUpdatePurchaseOrder();
  const reportError = useReportError();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { productList } = useSelector((state: RootState) => state.products);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<PurchaseOrderFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(purchaseOrderSchema as any),
    defaultValues: {
      supplierId: purchaseOrder.supplierId,
      expectedDeliveryDate: purchaseOrder.expectedDeliveryDate || "",
      terms: purchaseOrder.terms || "",
      tax: purchaseOrder.tax,
      shippingCost: purchaseOrder.shippingCost,
      currency: purchaseOrder.currency,
      notes: purchaseOrder.notes || "",
      lineItems: purchaseOrder.lineItems.map((item) => ({
        productId: item.productId,
        productName: item.productName,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        unit: item.unit,
        itemEntries: item.itemEntries || []
      }))
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "lineItems"
  });

  const watchedLineItems = watch("lineItems");

  useEffect(() => {
    if (open && purchaseOrder) {
      reset({
        supplierId: purchaseOrder.supplierId,
        expectedDeliveryDate: purchaseOrder.expectedDeliveryDate || "",
        terms: purchaseOrder.terms || "",
        tax: purchaseOrder.tax,
        shippingCost: purchaseOrder.shippingCost,
        currency: purchaseOrder.currency,
        notes: purchaseOrder.notes || "",
        lineItems: purchaseOrder.lineItems.map((item) => ({
          productId: item.productId,
          productName: item.productName,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          unit: item.unit,
          itemEntries: item.itemEntries || []
        }))
      });
    }
  }, [open, purchaseOrder, reset]);

  const handleProductChange = (
    index: number,
    productId: string,
    products: Array<{
      id: string;
      name: string;
      unit?: string;
      purchasePrice?: number;
    }>
  ) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      const currentLineItems = watch("lineItems");
      const updatedLineItems = currentLineItems.map((item, i) =>
        i === index
          ? {
              ...item,
              productId: product.id,
              productName: product.name,
              unit: product.unit,
              unitPrice: product.purchasePrice || 0
            }
          : item
      );
      reset({ ...watch(), lineItems: updatedLineItems });
    }
  };

  const onSubmit: SubmitHandler<PurchaseOrderFormData> = async (data) => {
    try {
      const processedLineItems = data.lineItems.map((item) => ({
        ...item,
        totalPrice: item.quantity * item.unitPrice
      }));
      await updatePurchaseOrder.mutateAsync({
        input: {
          orderId: purchaseOrder.id,
          supplierId: data.supplierId,
          expectedDeliveryDate: data.expectedDeliveryDate,
          deliveryAddress: data.deliveryAddress,
          terms: data.terms,
          tax: data.tax,
          shippingCost: data.shippingCost,
          notes: data.notes,
          lineItems: processedLineItems
        }
      });
      onClose();
    } catch (error) {
      reportError("Failed to update purchase order (modal)", error, {
        component: "EditPurchaseOrderModal",
        orderId: purchaseOrder.id
      });
    }
  };

  const isLoading = isSubmitting || updatePurchaseOrder.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("edit-purchase-order-title", "Edit Purchase Order")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          {/* Scrollable Content Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: matchMD ? 2 : 3,
              pt: 2,
              display: "flex",
              flexDirection: "column",
              "&::-webkit-scrollbar": {
                width: "8px"
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "10px"
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "#555"
                }
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: matchMD ? 1.5 : 2
              }}
            >
              <Controller
                name="supplierId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={t("edit-purchase-order-supplier-label", "Supplier")}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    error={!!errors.supplierId}
                    helperText={errors.supplierId?.message}
                    value={field.value || ""}
                    disabled={isLoading}
                  >
                    {SampleSuppliers.filter(
                      (supplier) => supplier.status !== "DELETED"
                    ).map((supplier) => (
                      <MenuItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="expectedDeliveryDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t(
                      "edit-purchase-order-expected-delivery-label",
                      "Expected Delivery Date"
                    )}
                    type="date"
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    InputLabelProps={{
                      shrink: true
                    }}
                    error={!!errors.expectedDeliveryDate}
                    helperText={errors.expectedDeliveryDate?.message}
                    disabled={isLoading}
                  />
                )}
              />

              <Controller
                name="terms"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t(
                      "edit-purchase-order-payment-terms-label",
                      "Payment Terms"
                    )}
                    placeholder={t(
                      "edit-purchase-order-payment-terms-placeholder",
                      "Ex: Net 30"
                    )}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    error={!!errors.terms}
                    helperText={errors.terms?.message}
                    disabled={isLoading}
                  />
                )}
              />

              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("edit-purchase-order-currency-label", "Currency")}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    error={!!errors.currency}
                    helperText={errors.currency?.message}
                    disabled={isLoading}
                  />
                )}
              />

              <Box
                sx={{
                  display: "flex",
                  gap: matchMD ? 1 : 2,
                  flexDirection: matchMD ? "column" : "row"
                }}
              >
                <Controller
                  name="tax"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("edit-purchase-order-tax-label", "Tax")}
                      type="number"
                      fullWidth
                      size={matchMD ? "small" : "medium"}
                      error={!!errors.tax}
                      helperText={errors.tax?.message}
                      disabled={isLoading}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || undefined)
                      }
                    />
                  )}
                />

                <Controller
                  name="shippingCost"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t(
                        "edit-purchase-order-shipping-cost-label",
                        "Shipping Cost"
                      )}
                      type="number"
                      fullWidth
                      size={matchMD ? "small" : "medium"}
                      error={!!errors.shippingCost}
                      helperText={errors.shippingCost?.message}
                      disabled={isLoading}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || undefined)
                      }
                    />
                  )}
                />
              </Box>

              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("edit-purchase-order-notes-label", "Notes")}
                    multiline
                    rows={3}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    error={!!errors.notes}
                    helperText={errors.notes?.message}
                    disabled={isLoading}
                  />
                )}
              />

              {/* Line Items Section */}
              <Box sx={{ mt: matchMD ? 2 : 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6" fontWeight="bold">
                    {t("edit-purchase-order-line-items-title", "Line Items")}
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() =>
                      append({
                        productId: "",
                        productName: "",
                        quantity: 1,
                        unitPrice: 0,
                        unit: "pcs",
                        itemEntries: []
                      })
                    }
                    variant="outlined"
                    size="small"
                    disabled={isLoading}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      borderColor: BRAND_COLOR,
                      color: BRAND_COLOR,
                      "&:hover": {
                        backgroundColor: BRAND_COLOR,
                        color: "white",
                        borderColor: BRAND_COLOR
                      }
                    }}
                  >
                    {t("edit-purchase-order-add-item-button", "Add Item")}
                  </Button>
                </Box>

                {fields.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={2}
                    sx={{ fontSize: matchMD ? "0.8rem" : "inherit" }}
                  >
                    {t(
                      "edit-purchase-order-no-line-items",
                      'No line items added. Click "Add Item" to add products.'
                    )}
                  </Typography>
                )}

                {errors.lineItems && (
                  <Typography
                    variant="body2"
                    color="error"
                    mb={2}
                    sx={{ fontSize: matchMD ? "0.8rem" : "inherit" }}
                  >
                    {errors.lineItems.message}
                  </Typography>
                )}

                {fields.map((field, index) => {
                  const lineItemError = errors.lineItems?.[index];
                  const lineItem = watchedLineItems?.[index];

                  return (
                    <Paper
                      key={field.id}
                      sx={{
                        p: matchMD ? 1.5 : 2,
                        mb: 2,
                        border: `1px solid ${BRAND_COLOR}20`,
                        borderRadius: 2
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ fontSize: matchMD ? "0.85rem" : "inherit" }}
                        >
                          {t("edit-purchase-order-item-label", "Item")}{" "}
                          {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => remove(index)}
                          disabled={isLoading}
                          sx={{ color: "error.main" }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>

                      <Box
                        display="flex"
                        flexDirection="column"
                        gap={matchMD ? 1.5 : 2}
                      >
                        <Controller
                          name={`lineItems.${index}.productId`}
                          control={control}
                          render={({ field: itemField }) => (
                            <TextField
                              {...itemField}
                              select
                              label={t(
                                "edit-purchase-order-product-label",
                                "Product"
                              )}
                              fullWidth
                              size={matchMD ? "small" : "medium"}
                              error={!!lineItemError?.productId}
                              helperText={lineItemError?.productId?.message}
                              disabled={isLoading}
                              onChange={(e) => {
                                itemField.onChange(e);
                                handleProductChange(
                                  index,
                                  e.target.value,
                                  productList
                                );
                              }}
                              value={itemField.value || ""}
                            >
                              {productList.map((product) => (
                                <MenuItem key={product.id} value={product.id}>
                                  {product.name} ({product?.unit})
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />

                        <Controller
                          name={`lineItems.${index}.quantity`}
                          control={control}
                          render={({ field: itemField }) => (
                            <TextField
                              {...itemField}
                              label={`${t("edit-purchase-order-quantity-label", "Quantity")} (${lineItem?.unit || "pcs"})`}
                              type="number"
                              fullWidth
                              size={matchMD ? "small" : "medium"}
                              error={!!lineItemError?.quantity}
                              helperText={lineItemError?.quantity?.message}
                              disabled={isLoading}
                              onChange={(e) =>
                                itemField.onChange(parseFloat(e.target.value))
                              }
                            />
                          )}
                        />

                        <Controller
                          name={`lineItems.${index}.unitPrice`}
                          control={control}
                          render={({ field: itemField }) => (
                            <TextField
                              {...itemField}
                              label={t(
                                "edit-purchase-order-unit-price-label",
                                "Unit Price"
                              )}
                              type="number"
                              fullWidth
                              size={matchMD ? "small" : "medium"}
                              error={!!lineItemError?.unitPrice}
                              helperText={lineItemError?.unitPrice?.message}
                              disabled={isLoading}
                              onChange={(e) =>
                                itemField.onChange(parseFloat(e.target.value))
                              }
                            />
                          )}
                        />

                        {lineItem?.quantity &&
                          lineItem?.unitPrice &&
                          lineItem.quantity > 0 &&
                          lineItem.unitPrice > 0 && (
                            <Box
                              display="flex"
                              justifyContent="flex-end"
                              mt={1}
                              sx={{
                                p: 1,
                                bgcolor: `${BRAND_COLOR}10`,
                                borderRadius: 1
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                sx={{
                                  fontSize: matchMD ? "0.8rem" : "inherit"
                                }}
                              >
                                {t("edit-purchase-order-total-label", "Total")}:{" "}
                                {new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: watch("currency") || "USD"
                                }).format(
                                  (lineItem.quantity || 0) *
                                    (lineItem.unitPrice || 0)
                                )}
                              </Typography>
                            </Box>
                          )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Fixed Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: matchMD ? "column" : "row",
              gap: matchMD ? 1 : 2,
              p: matchMD ? 2 : 3,
              pt: matchMD ? 1.5 : 2,
              borderTop: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
              backgroundColor: "background.paper"
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              fullWidth
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {t("edit-purchase-order-submit-button", "Update Order")}
            </LoadingButton>

            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
              fullWidth
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: "white",
                color: BRAND_COLOR,
                border: `1px solid ${BRAND_COLOR}`,
                "&:hover": {
                  backgroundColor: BRAND_HOVER,
                  color: "white",
                  borderColor: BRAND_HOVER
                }
              }}
            >
              {t("edit-purchase-order-cancel-button", "Cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditPurchaseOrderModal;
